import { useCallback } from 'react'
import { useQuery } from 'react-query'

import { getEInvoicingAddressingLines } from '@features/eInvoicing/query-api'

import { QueryKeys } from '../../../../../enums/queryKeys'
import { AddressingLineLookupStatus } from '../enums/addressingLineLookupStatus'
import { getAddressingLineLookupStatus } from '../utils/getAddressingLineLookupStatus'
import { getSirenFromAddressingLine } from '../utils/getSirenFromAddressingLine'
import { normalizeAddressingLine } from '../utils/normalizeAddressingLine'

type UseFetchEInvoicingAddressingLinesResponse = {
  fetchAddressingLines: () => Promise<AddressingLineLookupStatus | undefined>
  isLookupLoading: boolean
  lookupStatus?: AddressingLineLookupStatus
}

export const useFetchEInvoicingAddressingLines = (
  currentAddressingLine?: string,
): UseFetchEInvoicingAddressingLinesResponse => {
  const addressingLine = normalizeAddressingLine(currentAddressingLine ?? '')
  const siren = getSirenFromAddressingLine(addressingLine)

  const { data, error, isError, isFetched, isFetching, refetch } = useQuery(
    [QueryKeys.EInvoicingSirenAddressingLines, siren],
    () => getEInvoicingAddressingLines(siren),
    {
      enabled: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
      retry: false,
    },
  )

  const lookupStatus =
    isFetched && !isFetching
      ? getAddressingLineLookupStatus({ addressingLine, addressingLines: data?.data ?? [], error, isError })
      : undefined

  const fetchAddressingLines = useCallback(async () => {
    if (!siren) {
      return undefined
    }

    const { data: refetchedData, error: refetchedError, isError: hasRefetchFailed } = await refetch()

    return getAddressingLineLookupStatus({
      addressingLine,
      addressingLines: refetchedData?.data ?? [],
      error: refetchedError,
      isError: hasRefetchFailed,
    })
  }, [addressingLine, refetch, siren])

  return {
    fetchAddressingLines,
    isLookupLoading: isFetching,
    lookupStatus,
  }
}
